import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, map, catchError, throwError } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { ToastrService } from 'ngx-toastr';
import { JWTTokenServiceService } from 'src/app/public/services/token.service';

const SECRET_KEY = '12345678dsadsadas90abcdef1234567';

/**
 * Endpoints that require AES-encrypted request body and encrypted response decryption.
 * Add endpoint URL fragments here — if the request URL contains any of these strings,
 * encryption/decryption will be applied automatically.
 */
const ENCRYPTED_ENDPOINTS: string[] = [
  'getAllOrgConfig',
  'updateOrgConfig',
  'getOrgConfigById'
];

/** Returns true if the request URL matches one of the encrypted endpoints. */
function shouldEncryptEndpoint(url: string): boolean {
  if (typeof url !== 'string') return false;
  return ENCRYPTED_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

/**
 * After AES decrypt, plaintext is JSON.parse'd once. If the backend encrypted a
 * stringified body (legacy), plaintext can be a JSON *string* — parse again.
 * Objects/arrays from a single parse are returned as-is (no double-parse).
 */
function parseDecryptedApiBody(decrypted: any): any {
  if (typeof decrypted === 'string') {
    try {
      return JSON.parse(decrypted);
    } catch {
      return decrypted;
    }
  }
  return decrypted;
}

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  constructor(private toastService: ToastrService, private tokenService: JWTTokenServiceService) { }

  encrypt(data: any): string {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  }

  decrypt(ciphertext: string): any {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only apply encryption/decryption logic to endpoints listed in ENCRYPTED_ENDPOINTS
    if (!shouldEncryptEndpoint(req.url)) {
      return next.handle(req);
    }

    let token = this.tokenService.getToken();
    if (!token || token === 'undefined') {
      token = localStorage.getItem('getpublicToken') || '';
    }

    let modifiedReq: HttpRequest<any>;

    if (req.body instanceof FormData) {
      // FormData — do not encrypt, just attach auth header
      modifiedReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`)
      });
    } else {
      // Encrypt the request body and wrap in { payload }
      const encryptedBody = this.encrypt(req.body || {});
      modifiedReq = req.clone({
        body: { payload: encryptedBody },
        headers: req.headers.set('Authorization', `Bearer ${token}`)
      });
    }

    return next.handle(modifiedReq).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.body?.response) {
          const decrypted = this.decrypt(event.body.response);
          console.log('Decrypted Response:', decrypted);
          return event.clone({
            body: parseDecryptedApiBody(decrypted)
          });
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.error?.response) {
          try {
            const decryptedError = this.decrypt(error.error.response);
            const parsedError = parseDecryptedApiBody(decryptedError);

            const modifiedError = new HttpErrorResponse({
              error: parsedError,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url || undefined
            });

            console.error('Decrypted Error:', parsedError);
            return throwError(() => modifiedError);
          } catch (decryptError) {
            console.error('Failed to decrypt error response:', decryptError);
          }
        }

        return throwError(() => error);
      })
    );
  }
}
